import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { Palette, Plus } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@aula/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { ThemeCard } from "./theme-card";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ThemeRow = components["schemas"]["ThemeDTO"];

export function ThemeManager() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  // Mirror of `Capability::ManageContent`. READ remains intact: `read_only`
  // still lists all themes and their diagnostics.
  const canWrite = () => can("content.write");
  const themesQuery = createApiQuery<ThemeRow[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "themes"],
    async () => api.GET("/api/admin/theme/listAll"),
    () => ({ staleTime: 30_000 }),
  );

  const toggleMutation = createApiMutation(async (vars: { themeId: string; isActive: boolean }) =>
    api.POST("/api/admin/theme/toggleActive", { body: vars }),
  );

  const deleteMutation = createApiMutation(async (vars: { themeId: string }) =>
    api.POST("/api/admin/theme/delete", { body: vars }),
  );

  const duplicateMutation = createApiMutation(async (vars: { themeId: string }) =>
    api.POST("/api/admin/theme/duplicate", { body: vars }),
  );

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await toggleMutation.mutateAsync({ themeId: id, isActive });
      await themesQuery.refetch();
    } catch (err) {
      console.error("Error toggling theme:", err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync({ themeId: id });
      await themesQuery.refetch();
    } catch (err) {
      console.error("Error deleting theme:", err);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateMutation.mutateAsync({ themeId: id });
      await themesQuery.refetch();
    } catch (err) {
      console.error("Error duplicating theme:", err);
    }
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Palette class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Theme Management`)}</h2>
        </div>
        <Show when={canWrite()}>
          <Button class="gap-2">
            <Plus class="h-4 w-4" />
            {t(msg`New Theme`)}
          </Button>
        </Show>
      </div>

      {/* P23/I3 · «no hay respuesta» ≠ «no hay nada»: en error `isLoading`
          vale false, así que el guard de abajo dejaba pasar y la pantalla
          pintaba el estado VACÍO. Hay que ENVOLVER el bloque, no encadenar
          otra condición al mismo `<Show>` (pintaría error Y esqueleto). */}
      <Show when={themesQuery.isError}>
        <QueryErrorState
          testId="admin-themes-error"
          compact
          onRetry={() => void themesQuery.refetch()}
        />
      </Show>
      <Show when={!themesQuery.isError}>
        <Show
          when={!themesQuery.isLoading}
          fallback={
            <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <For each={Array.from({ length: 6 }, () => 0)}>
                {() => <Skeleton class="h-48 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <For each={themesQuery.data ?? []}>
              {(theme) => (
                <ThemeCard
                  theme={theme}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                  isAnyPending={
                    toggleMutation.isPending ||
                    deleteMutation.isPending ||
                    duplicateMutation.isPending
                  }
                  canWrite={canWrite()}
                  t={t}
                />
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
